/**
 * @file Post-processing chain (GDD §Fase 4 — iluminación avanzada).
 *
 * Scene → bloom → grade → screen. Bloom is what makes every emissive material
 * in the palette read as an actual light source, and it runs at a fraction of
 * the viewport because its whole job is a blur.
 *
 * All passes come from Three.js' own addons, so the "Three.js is the only
 * framework" rule still holds. If `post.enabled` is false the class is never
 * constructed and the game draws straight through the renderer.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { NeonGradeShader } from './NeonGradeShader.js';
import { pulseFxDescendingValue, samplePulseFxIntensity } from './PulseEffect.js';
import { getCameraTuning } from '../config/CameraTuning.js';
import { damp } from '../utils/MathUtils.js';

export class PostProcessing {
  /**
   * @param {import('./Renderer.js').Renderer} renderer Renderer wrapper.
   * @param {THREE.Scene} scene Scene to draw.
   * @param {THREE.Camera} camera Active camera.
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   */
  constructor(renderer, scene, camera, config) {
    /** @type {import('./Renderer.js').Renderer} @private */
    this._renderer = renderer;
    /** @type {import('../config/GameConfig.js').GameConfig} @private */
    this._config = config;

    const size = this._viewportSize();

    /** @type {EffectComposer} */
    this.composer = new EffectComposer(renderer.instance);
    this.composer.setPixelRatio(renderer.instance.getPixelRatio());
    this.composer.setSize(size.width, size.height);

    this.composer.addPass(new RenderPass(scene, camera));

    const bloomSettings = config.post.bloom;
    /** @type {UnrealBloomPass} */
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(
        size.width * bloomSettings.resolutionScale,
        size.height * bloomSettings.resolutionScale
      ),
      bloomSettings.strength,
      bloomSettings.radius,
      bloomSettings.threshold
    );
    this.composer.addPass(this.bloom);

    const gradeSettings = config.post.grade;
    /** @type {ShaderPass} */
    this.grade = new ShaderPass(NeonGradeShader);
    this.grade.uniforms.uVignette.value = gradeSettings.vignette;
    this.grade.uniforms.uAberration.value = gradeSettings.chromaticAberration;
    this.grade.uniforms.uScanline.value = gradeSettings.scanline;
    this.grade.uniforms.uSaturation.value = gradeSettings.saturation;
    this.grade.uniforms.uFisheye.value = getCameraTuning().fisheye;
    this.grade.uniforms.uResolution.value = [size.width, size.height];
    this.composer.addPass(this.grade);

    // Tone mapping and colour space conversion, once, at the very end.
    this.composer.addPass(new OutputPass());

    /** @type {number} Smoothed pulse FX intensity, 0..1. Starts calm. @private */
    this._pulseValue = 0;

    /** @type {() => void} @private */
    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
  }

  /**
   * @returns {{ width: number, height: number }} Current CSS viewport size.
   * @private
   */
  _viewportSize() {
    // Delegates to the renderer, which guarantees a non-zero size.
    return this._renderer.size;
  }

  /**
   * Resizes the chain to match the canvas.
   * @returns {void}
   */
  resize() {
    const { width, height } = this._viewportSize();
    this.composer.setPixelRatio(this._renderer.instance.getPixelRatio());
    this.composer.setSize(width, height);
    this.grade.uniforms.uResolution.value = [width, height];
  }

  /**
   * Draws one frame through the chain. Also drives the pulse-linked grade
   * (GDD-adjacent: aberration/glow/saturation ride the player's kill-timer
   * ratio, see `PulseEffect.js`) when `config.post.pulse.enabled` is set.
   *
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   */
  render(ctx) {
    const { time, config, player } = ctx;
    this.grade.uniforms.uTime.value = time.elapsed;
    this.grade.uniforms.uFisheye.value = getCameraTuning().fisheye;

    const pulseCfg = config.post.pulse;
    if (pulseCfg?.enabled) {
      const target = samplePulseFxIntensity(player);
      this._pulseValue = damp(this._pulseValue, target, pulseCfg.smoothing, time.delta);
      this.grade.uniforms.uAberration.value = pulseFxDescendingValue(
        pulseCfg.aberration,
        this._pulseValue
      );
      this.grade.uniforms.uSaturation.value = pulseFxDescendingValue(
        pulseCfg.saturation,
        this._pulseValue
      );
      this.bloom.strength = pulseFxDescendingValue(pulseCfg.glow, this._pulseValue);
    }

    this.composer.render();
  }

  /**
   * Releases render targets and listeners.
   * @returns {void}
   */
  dispose() {
    window.removeEventListener('resize', this._onResize);
    this.bloom.dispose();
    this.composer.dispose();
  }
}
